import { inject, injectable } from "inversify";
import { from, Observable } from "rxjs";

import {
  ApiError,
  DecodeError,
  HttpError,
  NetworkError
} from "@apphq/apphq-client/src/errors/errors";
import { Headers as RawHeaders } from "@apphq/apphq-client/src/Headers";
import { HttpResponse } from "@apphq/apphq-client/src/HttpResponse";
import { IHttpClient } from "@apphq/apphq-client";

import { LanguageSelection } from "@/i18n/LanguageSelection";

@injectable()
export class HttpClient implements IHttpClient {
  constructor(
    @inject(LanguageSelection) private languageSelection: LanguageSelection
  ) {}

  public get(url: string, headers?: RawHeaders): Observable<HttpResponse> {
    return from(
      this.performNetworkCall(url, "get", undefined, new Headers(headers))
    );
  }

  public post(
    url: string,
    body?: {} | FormData,
    headers?: RawHeaders
  ): Observable<HttpResponse> {
    return from(
      this.performNetworkCall(
        url,
        "post",
        this.getJsonBody(body),
        new Headers(headers)
      )
    );
  }

  public put(
    url: string,
    body?: {},
    headers?: RawHeaders
  ): Observable<HttpResponse> {
    return from(
      this.performNetworkCall(
        url,
        "put",
        this.getJsonBody(body),
        new Headers(headers)
      )
    );
  }

  public delete(url: string, headers?: RawHeaders): Observable<HttpResponse> {
    return from(
      this.performNetworkCall(url, "delete", undefined, new Headers(headers))
    );
  }

  private getJsonBody(body?: {} | FormData) {
    if (body instanceof FormData) {
      return body;
    } else if (body === undefined) {
      return undefined;
    } else {
      return JSON.stringify(body);
    }
  }

  /**
   * Parse the Content-Type header according to
   * https://tools.ietf.org/html/rfc7231#section-3.1.1.1
   *
   * Note: Headers.get could return a list of multiple content types. We don’t handle this currently.
   */
  private getContentType(headers?: Headers) {
    const header = headers == null ? null : headers.get("Content-Type");
    if (header == null) {
      return {
        main: undefined,
        charset: undefined
      };
    }
    const parts = header.split(";").map(str => str.trim());
    const contentType: { [key: string]: string } = {};

    const main = parts.shift();
    if (main !== undefined) {
      contentType.main = main;
    }

    for (const part of parts) {
      let [key, value] = part.split("=", 2);
      if (value.startsWith('"') && value.endsWith('"')) {
        value = value.substring(1, value.length - 1);
      }
      if (key !== undefined && value !== undefined) {
        contentType[key] = value;
      }
    }
    return contentType;
  }

  private async performNetworkCall(
    url: string,
    method: string,
    body?: FormData | string,
    headers: Headers = new Headers()
  ): Promise<HttpResponse> {
    /* Set language selected by user */
    if (!headers.has("Accept-Language")) {
      headers.set("Accept-Language", this.languageSelection.language);
    }

    const request = new Request(url, {
      method,
      body,
      headers
    });

    let response;
    try {
      response = await window.fetch(request);
    } catch (error) {
      if (error instanceof TypeError) {
        throw new NetworkError(request, error);
      } else {
        throw error;
      }
    }

    const contentType = this.getContentType(response.headers);
    let payload;
    try {
      if (contentType.main === "application/json") {
        payload = await response.json();
      } else if (
        contentType.main === "application/octet-stream" ||
        (contentType.charset && contentType.charset.toLowerCase() !== "utf-8")
      ) {
        payload = await response.blob();
      } else {
        payload = await response.text();
      }
    } catch (error) {
      throw new DecodeError(response, request, error);
    }

    const httpResponse: HttpResponse = { ...response, payload };

    if (response.ok) {
      return httpResponse;
    }

    if (payload.rhenusHttpStatus) {
      throw new ApiError(payload.rhenusHttpStatus, response, request);
    } else {
      throw new HttpError(response, request);
    }
  }
}
